<template>
  <div v-if="showSwitcher" class="branch-switcher">
    <span class="material-icons branch-switcher__icon" aria-hidden="true">storefront</span>
    <div class="branch-switcher__control">
      <span class="branch-switcher__label">
        {{ $t('branches.branch') }}
      </span>
      <span class="branch-switcher__value">{{ selectedBranchLabel }}</span>
    </div>
    <span class="material-icons branch-switcher__arrow" aria-hidden="true">expand_more</span>
    <select
      id="navbar-branch-select"
      class="branch-switcher__select"
      :aria-label="$t('branches.branch')"
      :value="activeBranchId || ''"
      @change="onChange"
    >
      <option v-if="canOfferAllBranches" value="">{{ $t('branches.allBranches') }}</option>
      <option v-for="b in options" :key="b.id" :value="b.id">{{ b.name }}</option>
    </select>
  </div>
</template>

<script>
export default {
  name: 'BranchSwitcher',
  computed: {
    branches() {
      return this.$store.getters.tenantBranches;
    },
    branchAccess() {
      return this.$store.state.auth.branchAccess || {};
    },
    canAccessAllBranches() {
      return !!this.branchAccess.canAccessAllBranches;
    },
    // Anyone with more than one branch has something to consolidate, so the combined view is theirs
    // too — it just means "all of mine" rather than "all of the club's".
    canOfferAllBranches() {
      return this.canAccessAllBranches || this.options.length > 1;
    },
    activeBranchId() {
      return this.$store.state.auth.activeBranchId;
    },
    selectedBranchLabel() {
      if (!this.activeBranchId) return this.$t('branches.allBranches');
      const branch = this.branches.find(
        (item) => String(item.id) === String(this.activeBranchId),
      );
      return branch ? branch.name : this.$t('branches.allBranches');
    },
    options() {
      if (this.canAccessAllBranches) return this.branches;
      const ids = this.branchAccess.accessibleBranchIds || [];
      return this.branches.filter((b) => ids.some((id) => String(id) === String(b.id)));
    },
    showSwitcher() {
      // Only when there is a real choice between branches (hidden for single-branch tenants).
      const count = this.options.length + (this.canOfferAllBranches ? 1 : 0);
      return this.$store.getters.isMultiBranchTenant && this.branches.length > 1 && count > 1;
    },
  },
  methods: {
    onChange(e) {
      const val = e.target.value || null;
      if (String(val || '') === String(this.activeBranchId || '')) return;
      this.$store.commit('changeActiveBranch', val);
      // Full reload so every cached grid/report re-fetches under the newly selected branch.
      window.location.reload();
    },
  },
};
</script>

<style lang="scss" scoped>
.branch-switcher {
  align-items: center;
  align-self: center;
  background: #f4f9fc;
  border: 1px solid #d9e9f3;
  border-radius: 10px;
  display: flex;
  gap: 9px;
  margin-inline: 10px;
  max-width: 240px;
  min-height: 44px;
  padding: 5px 10px;
  position: relative;
  width: 240px;

  &__icon {
    align-items: center;
    background: #e3f3f8;
    border-radius: 7px;
    color: #008eaa;
    display: flex;
    flex: 0 0 auto;
    font-size: 19px;
    height: 30px;
    justify-content: center;
    width: 30px;
  }

  &__control {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
  }

  &__value {
    color: var(--fitnet-heading-color, #274967);
    display: block;
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.1;
    overflow: hidden;
    text-align: start;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    color: #31506f;
    flex: 0 0 auto;
    font-size: 20px;
  }

  &__label {
    color: #7890a6;
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1;
    margin: 0 0 2px;
    text-transform: uppercase;
  }

  &__select {
    appearance: auto;
    background: transparent;
    border: 0;
    color: var(--fitnet-heading-color, #274967);
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 700;
    height: 100%;
    inset: 0;
    line-height: 1.1;
    max-width: none;
    min-height: 100%;
    min-width: 0;
    opacity: 0;
    outline: 0;
    padding: 0;
    position: absolute;
    text-align: start;
    text-overflow: ellipsis;
    width: 100%;
    z-index: 2;

    &:focus {
      box-shadow: none;
    }

    option {
      direction: inherit;
      text-align: start;
      width: 100%;
    }
  }

  &:focus-within {
    border-color: #76c6d8;
    box-shadow: 0 0 0 3px rgba(0, 152, 184, 0.12);
  }
}

@media (max-width: 767px) {
  .branch-switcher {
    margin-inline: 5px;
    max-width: 170px;
    padding-inline: 7px;
    width: 170px;

    &__label {
      display: none;
    }

    &__select {
      max-width: none;
      min-width: 0;
      width: 100%;
    }
  }
}
</style>
